import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Zap, DollarSign, TrendingUp, Target } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const AGVMarketArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-purple-500 text-white">
                <Zap className="h-3 w-3 mr-1" />
                {t('article.agv.badge')}
              </Badge>
              <span className="text-gray-500">{t('article.agv.date')}</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.agv.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.agv.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://imgs.newseditor.aws.kuehne-nagel.cloud/a96b9e0b-2b54-4d53-8f7c-ff03237ea8f1.jpg" 
              alt="COSCO CSSC mega shipbuilding order announcement" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                  {t('agv.stats.value.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">{t('agv.stats.value.value')}</div>
                <p className="text-sm text-gray-600">{t('agv.stats.value.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                  {t('agv.stats.growth.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('agv.stats.growth.value')}</div>
                <p className="text-sm text-gray-600">{t('agv.stats.growth.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Target className="h-5 w-5 mr-2 text-purple-600" />
                  {t('agv.stats.driver.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-purple-600">{t('agv.stats.driver.value')}</div>
                <p className="text-sm text-gray-600">{t('agv.stats.driver.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('agv.section1.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('agv.section1.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('agv.section2.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('agv.section2.content')}
            </p>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('agv.section2.list')}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('agv.section3.title')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <div className="whitespace-pre-line text-sm text-gray-700 mb-4">
                {t('agv.section3.northamerica')}
              </div>
              <div className="whitespace-pre-line text-sm text-gray-700 mb-4">
                {t('agv.section3.europe')}
              </div>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('agv.section3.asiapacific')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('agv.section4.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('agv.section4.content')}
            </p>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('agv.section4.companies')}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('agv.section5.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('agv.section5.content')}
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('agv.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={() => window.open('https://mykn.kuehne-nagel.com/news/article/coscocssc-unveil-record-yuan50bn-mega-order', '_blank')}
              >
                View Original Source
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default AGVMarketArticle;